import React from "react";
import Image from "components/image";
import { Swiper, SwiperSlide } from "components/swiper";
import { Navigation, Pagination } from "swiper/modules";
import { ReactComponent as FiveStars } from "svgs/five-stars.svg";
import { ReactComponent as VerifiedIcon } from "assets/svgs/verified.svg";
import { ReactComponent as Arrow } from "svgs/middle-size-arrow.svg";
import ButtonLink from "components/button-link";
import clsx from "clsx/lite";

const DefaultLayout = ({ data }) => (
	<div className={clsx("w-full flex gap-6 overflow-auto no-scrollbar",
		data.isFluid && "md:standard-section-width horizontal-padding")}>
		{data.items?.map((item, index) => (
			<div key={index} className="flex flex-col gap-2">
				{item.image && <Image image={item.image} className="min-w-64 w-72 2xl:w-full h-64" />}
				<p className="line-clamp-1 font-bold">{item.title}</p>
				<FiveStars />
				{item.description && <p className="text-secondary text-sm line-clamp-3">{item.description}</p>}
			</div>
		))}
	</div>
);

const CarouselLayout = ({ data }) => {
	const { items, oneSlidePerView, hideStarRating, hideDots } = data;
	const swiperSettings = {
		modules: [Navigation, Pagination],
		loop: !oneSlidePerView,
		navigation: {
			nextEl: ".reviews-right-arrow",
			prevEl: ".reviews-left-arrow",
			disabledClass: "opacity-50",
		},
		pagination: hideDots ? false : {
			el: ".swiper-pagination",
			clickable: true,
		},
		centeredSlides: true,
		slidesPerView: oneSlidePerView ? 1 : 1.2,
		spaceBetween: 16,
		breakpoints: {
			320: {
				slidesPerView: oneSlidePerView ? 1 : 1.2
			},
			640: {
				slidesPerView: oneSlidePerView ? 1 : 2,
				spaceBetween: 24,
			},
			1024: {
				slidesPerView: oneSlidePerView ? 1 : 3.5,
				spaceBetween: 24,
			},
		},
	};
	return (
		<div className={clsx("w-full flex flex-col items-center text-center pt-5 md:pt-10 pb-3 overflow-hidden",
			oneSlidePerView && "max-w-screen-md")}>
			<Swiper {...swiperSettings} className="w-full mb-4 overflow-visible">
				{items.map((item, index) => (
					<SwiperSlide
						key={index}
						className={
							clsx("px-3 h-full flex w-11/12 mr-2 sm:w-1/2 lg:w-1/4 bg-white md:mr-6",
								oneSlidePerView ? "border border-gray-400" : "shadow-xl")}>
						<div className="flex flex-col p-4 pt-6 justify-start w-full gap-3 my-auto">
							{item.image &&
								<Image image={item.image}
									className="max-w-80 max-h-80 aspect-square self-center object-cover mb-2" />}
							{!hideStarRating && <FiveStars className="mx-auto scale-125" />}
							<div className="flex flex-col gap-3">
								<h4 className="font-bold font-base tracking-tight">{item.title}</h4>
								{item.description && <p className="tracking-tight">{item.description}</p>}
							</div>
							{item.author &&
								<p className={clsx(hideStarRating ? "font-black" : "text-gray-700 italic")}>{item.author}</p>}
							{item.logo &&
								<Image image={item.logo} className="h-12 self-center *:!object-contain" />}
						</div>
					</SwiperSlide>
				))}
			</Swiper>
			<div className="custom-reviews-arrows flex justify-center items-center space-x-3 mt-5">
				<button className="reviews-left-arrow cursor-pointer border border-black p-1.5 bg-black">
					<Arrow className="rotate-180 invert" />
				</button>
				{!oneSlidePerView && <div className="swiper-pagination relative mt-2"></div>}
				<button className="reviews-right-arrow cursor-pointer border border-black p-1.5 bg-black">
					<Arrow className="invert" />
				</button>
			</div>
		</div>
	);
};

const GridLayout = ({ data }) => (
	<div className={clsx(
		"w-full overflow-auto no-scrollbar grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6",
		data.isFluid && "horizontal-padding standard-section-width mx-a"
	)}>
		{data.items.map((item, index) => (
			<div key={index}
				className="flex flex-col gap-2 border border-gray-300 p-6 bg-white"
			>
				<div className="flex items-center justify-between">
					<div className="flex items-center w-full justify-between">
						<FiveStars />
						{item.timeAgo && <p className="text-gray-500 text-xs ml-2">{item.timeAgo}</p>}
					</div>
				</div>
				{item.description &&
					<p className="text-black text-md">
						{item.description}
					</p>
				}
				<div className="flex items-center gap-2 justify-between md:justify-start">
					<p className="line-clamp-1 mr-2 text-xl">{item.title}</p>
					<div className="flex items-center bg-lightgrey rounded-full px-2 py-1">
						<VerifiedIcon />
						<span className="ml-1 text-xs text-black">VERIFIED BUYER</span>
					</div>
				</div>
				{item.image &&
					<div className="flex justify-center mt-auto">
						<Image image={item.image} className="max-w-full h-64 object-cover" />
					</div>}
			</div>
		))}
	</div>
);


const Layout = ({ data }) => {
	switch (data.layout) {
		case "carousel":
			return <CarouselLayout data={data} />;
		case "grid":
			return <GridLayout data={data} />;
		default:
			return <DefaultLayout data={data} />;
	}
};

const CustomReviews = ({ data }) => {
	const { layout, backgroundImage, settings = {} } = data;
	const isFluid = settings?.isFluid;

	return (
		<div id={layout != "carousel" ? "reviews-widget" : ""}
			style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {}} className="w-full flex justify-center">
			<div className="flex-center flex-col w-full gap-6">
				<div className={clsx("w-full flex flex-col gap-3", isFluid && "standard-section-width horizontal-padding")}>
					{data.title &&
						<h2 className={clsx(layout == "grid" && "md:text-center font-base", layout == "carousel" && "md:text-center")}>
							{data.title}
						</h2>}
					{data.subtitle &&
						<p className="text-secondary line-clamp-1">{data.subtitle}</p>}
				</div>
				<Layout data={data} />
				{data.cta &&
					<div className="w-full flex justify-center horizontal-padding">
						<ButtonLink data={data.cta} className="w-full text-center px-12 py-4 sm:w-auto sm:flex font-normal mt-6" />
					</div>}
			</div>
		</div>
	);
};

export default CustomReviews;